import campaignsApi from 'common/src/modules/campaigns/campaigns.api';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignSetupAgentToolName} from '../../../../campaignSetupAgent.enums';

const getProductMatchingSignalToolSchema = z.object({
  productType: z.number().describe('The feed product type'),
  signalName: z.string().describe('Short description of the signal to search for'),
});

export default function getProductMatchingSignalTool(store: CLStore) {
  async function execute({
    input: {signalName, productType},
  }: ToolExecuteParams<typeof getProductMatchingSignalToolSchema>): Promise<ToolResult> {
    const {
      campaign: {id: campaignId},
      versionId,
    } = store.getState().campaignPage.campaignTree;

    if (!campaignId) {
      return toolResult('Campaign ID is required to get content signals');
    }

    if (!versionId) {
      return toolResult('Version ID is required to get content signals');
    }

    try {
      const signals = await campaignsApi.getSignalsByNameForProductCard({
        campaignId,
        signalName,
        versionId,
        productType,
      });

      const signalsMetadata = signals.map((signal) => ({
        signalKey: signal.value,
        signalName: signal.name,
        category: signal.category,
      }));

      return toolResult(
        JSON.stringify({
          signals: signalsMetadata,
          instructions:
            signalsMetadata.length > 0
              ? `Multiple signals found, ask the user for clarification.`
              : '',
        }),
      );
    } catch (error) {
      return toolResult(
        `Error retrieving available signals: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  return tool({
    name: CampaignSetupAgentToolName.GetProductMatchingSignal,
    displayName: 'Getting available signals',
    description: 'Retrieve available signals for matching strategy configuration in product card',
    execute,
    inputSchema: getProductMatchingSignalToolSchema,
  });
}
